import React from "react";
import {
  Box,
  Drawer,
  IconButton,
  Stack,
  Skeleton,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useQuery } from "@tanstack/react-query";
import apiService from "../../services/api";
import { CardHeading, CardSecondaryText } from "./MediaKitComponents";
import { MediaKitYoutubeCatalogList } from "./MediaKitYoutubeCatalogList";
import { MediaKitMetricsWindowCaption } from "./MediaKitMetricsWindowCaption";
import { useTranslation } from "react-i18next";

export type MediaKitYoutubeCatalogDrawerProps = {
  open: boolean;
  onClose: () => void;
  podcastSlug: string;
  companySlug?: string;
  selectedMonths?: string[];
  programSlug?: string;
  format?: "longForm" | "shorts";
};

export const MediaKitYoutubeCatalogDrawer: React.FC<
  MediaKitYoutubeCatalogDrawerProps
> = ({
  open,
  onClose,
  podcastSlug,
  companySlug,
  selectedMonths,
  programSlug,
  format = "longForm",
}) => {
  const { t } = useTranslation("mediaKit");
  const { data, isFetching, error } = useQuery({
    queryKey: [
      "media-kit-youtube-catalog",
      podcastSlug,
      companySlug ?? "",
      (selectedMonths ?? []).join(","),
      programSlug ?? "",
      format,
    ],
    queryFn: () =>
      apiService.getPodcastMediaKitYoutubeCatalog(podcastSlug, {
        months: selectedMonths,
        programSlug,
        companySlug,
        format,
      }),
    enabled: open && Boolean(podcastSlug),
    staleTime: 5 * 60 * 1000,
  });

  const videos = data?.videos ?? [];
  const title = format === "shorts" ? "YouTube Shorts" : "YouTube Videos";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: "100%", sm: 440 }, bgcolor: "background.paper" },
      }}
    >
      <Box
        sx={{
          p: 2.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <CardHeading variant="h6">{title}</CardHeading>
        <IconButton onClick={onClose} aria-label={t("close")} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ p: 2, overflow: "auto" }}>
        <Box sx={{ mt: -1.5, mb: 1 }}>
          <MediaKitMetricsWindowCaption format={format} />
        </Box>
        {isFetching && videos.length === 0 ? (
          <Stack spacing={2}>
            {[0, 1, 2].map((i) => (
              <Stack key={i} direction="row" spacing={1.5}>
                <Skeleton variant="rounded" width={120} height={68} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton width="80%" />
                  <Skeleton width="50%" />
                </Box>
              </Stack>
            ))}
          </Stack>
        ) : error ? (
          <Alert severity="error">{t("catalogError")}</Alert>
        ) : videos.length === 0 ? (
          <CardSecondaryText variant="body2">
            {t("noVideos")}
          </CardSecondaryText>
        ) : (
          <MediaKitYoutubeCatalogList videos={videos} />
        )}
      </Box>
    </Drawer>
  );
};
